import DashboardCard from '@/features/company/components/DashboardCard';
import { useGetUserShares } from '../api/getAllUserShares';
import { Alert, Button, Empty, Flex, Spin, Tabs, Typography } from 'antd';
import { formatNumber } from '@/utils/formatNumber';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import themeToken from '@/lib/theme/tokens';
import IndirectShares from './IndirectShares';

const SharesContainer = styled.section`
  width: 100%;
  min-width: 0;
  padding: 24px;
  background: ${themeToken.white};
  border: 1px solid ${themeToken['branding-natural-3']};
  border-radius: 8px;

  .ant-tabs-nav {
    margin-block-start: -34px;
  }

  && .ant-tabs-nav .ant-tabs-nav-wrap {
    flex: 0 1 auto;
    margin-inline-start: auto;
  }

  && .ant-tabs-tab.ant-tabs-tab-active .ant-tabs-tab-btn {
    color: ${themeToken.colorPrimary};
  }

  @media (max-width: 600px) {
    padding: 16px;

    .ant-tabs-nav {
      margin-block-start: 12px;
    }
  }
`;

const { Title, Text } = Typography;

const UserShares = () => {
  const { data: userShares, isLoading, error, refetch } = useGetUserShares({});
  const directShares = userShares?.data;
  const indirectHoldings = userShares?.indirect_holdings;
  const { t: sharesText } = useTranslation('profile', { keyPrefix: 'shares' });

  const { t } = useTranslation('captable', {
    keyPrefix: 'overview',
  });

  const status = isLoading ? (
    <Flex justify="center" align="center" style={{ minHeight: 240 }}>
      <Spin />
    </Flex>
  ) : error ? (
    <Alert
      type="error"
      showIcon
      message={sharesText('loadError')}
      action={<Button onClick={() => void refetch()}>{sharesText('retry')}</Button>}
    />
  ) : null;

  return (
    <SharesContainer>
      <Title level={4} style={{ margin: 0 }}>
        {sharesText('title')}
      </Title>
      <Tabs
        defaultActiveKey="direct"
        destroyInactiveTabPane
        items={[
          {
            key: 'direct',
            label: sharesText('direct'),
            children:
              status ||
              (!directShares?.length ? (
                <Empty description={sharesText('empty')} />
              ) : (
                <Flex gap={16} wrap="wrap">
                  {directShares.map((share, index) => (
                    <DashboardCard
                      key={index}
                      hasFlex={false}
                      dark="true"
                      style={{ width: 224, maxWidth: '100%' }}
                      subtitleStyle={{ color: '#bdabff', fontSize: '16px' }}
                    >
                      <Flex justify="space-between" gap={8} vertical>
                        <Title level={5} style={{ margin: 0, overflowWrap: 'anywhere' }}>
                          {share.companyName}
                        </Title>
                        <Text style={{ fontSize: 30, color: '#fff' }} strong>
                          {formatNumber(share.user_ownership ?? 0, { maximumFractionDigits: 2 })} %
                        </Text>
                        <Flex gap={2} vertical className="details">
                          <Text>{`${formatNumber(share.common_shares)} ${t('commonShares')}`}</Text>
                          <Text>{`${formatNumber(share.preferred_shares)} ${t('preferredShares')}`}</Text>
                          <Text>{`${formatNumber(share.options)} ${t('options')}`}</Text>
                          <Text>{`${formatNumber(share.convertible_instruments)} ${t('convertibleInstrument')}`}</Text>
                        </Flex>
                      </Flex>
                    </DashboardCard>
                  ))}
                </Flex>
              )),
          },
          {
            key: 'indirect',
            label: sharesText('indirect'),
            children:
              status ||
              (indirectHoldings?.length ? (
                <IndirectShares holdings={indirectHoldings} />
              ) : (
                <Empty description={sharesText('emptyIndirect')} />
              )),
          },
        ]}
      />
    </SharesContainer>
  );
};

export default UserShares;
